import { memo, ReactNode, useCallback, useEffect, useRef } from 'react'

interface IProps {
  children: ReactNode
  className?: string
  'data-component'?: string
  id?: string
  onClickOutside?: EventListener
}

export const ClickOutside = memo(function ClickOutside({ children, id, onClickOutside, className, ...rest }: IProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const isAllowRef = useRef<boolean>(true)
  const targetRef = useRef<object | null>(null)

  const handleEvent: EventListener = useCallback(
    (e) => {
      switch (e.type) {
        case 'mousedown':
          isAllowRef.current = true
          targetRef.current = e.target
          break
        case 'mouseup':
          if (targetRef.current !== e.target) {
            isAllowRef.current = false
          }
          break
        default:
          if (
            e.target instanceof Element &&
            isAllowRef.current &&
            onClickOutside &&
            containerRef.current &&
            !containerRef.current.contains(e.target)
          ) {
            onClickOutside(e)
          }
      }
    },
    [onClickOutside]
  )

  const subscribe = useCallback(() => {
    setTimeout(() => {
      const container = (id && document.getElementById(id)) || document

      container.addEventListener('mousedown', handleEvent, true)
      container.addEventListener('mouseup', handleEvent, true)
      container.addEventListener('click', handleEvent, true)
    }, 0)
  }, [id, handleEvent])

  const unsubscribe = useCallback(() => {
    const container = (id && document.getElementById(id)) || document

    container.removeEventListener('mousedown', handleEvent, true)
    container.removeEventListener('mouseup', handleEvent, true)
    container.removeEventListener('click', handleEvent, true)
  }, [id, handleEvent])

  useEffect(() => {
    if (onClickOutside) {
      subscribe()
    } else {
      unsubscribe()
    }

    return unsubscribe
  }, [unsubscribe, subscribe, onClickOutside])

  return (
    <div className={className} id={id} {...rest} ref={containerRef}>
      {children}
    </div>
  )
})
